<template>
  <div class="rmr-site-picker">
    <div class="rmr-site-picker__toolbar">
      <rmr-text-field
        :value="search"
        :prepend-inner-icon="icons.mdiMagnify"
        :placeholder="searchPlaceholder"
        :aria-label="searchPlaceholder"
        :disabled="disabled || loading"
        class="rmr-site-picker__search"
        @input="search = $event"
      />
      <span class="rmr-site-picker__count" aria-live="polite">
        {{ filteredSites.length }}
        {{ filteredSites.length > 1 ? "sites" : "site" }}
      </span>
    </div>

    <div v-if="loading" class="rmr-site-picker__loading" role="status">
      <v-skeleton-loader
        v-for="index in effectivePageSize"
        :key="index"
        type="list-item-avatar-two-line"
      />
    </div>

    <div
      v-else-if="filteredSites.length"
      class="rmr-site-picker__grid"
      role="group"
      :aria-label="ariaLabel"
    >
      <button
        v-for="site in paginatedSites"
        :key="site.id"
        type="button"
        class="rmr-site-picker__card"
        :class="{ 'rmr-site-picker__card--selected': site.id === value }"
        :aria-pressed="site.id === value ? 'true' : 'false'"
        :disabled="disabled"
        @click="selectSite(site.id)"
      >
        <span
          class="rmr-site-picker__avatar"
          :style="{
            color: site.color || colors.indigoDark,
            backgroundColor: site.background || colors.indigoBackground,
          }"
        >
          {{ site.initials || initials(site.name) }}
        </span>

        <span class="rmr-site-picker__content">
          <strong>{{ site.name }}</strong>
          <small v-if="site.domain">{{ site.domain }}</small>
        </span>

        <span
          class="rmr-site-picker__check"
          :class="{
            'rmr-site-picker__check--active': site.id === value,
          }"
          aria-hidden="true"
        >
          ✓
        </span>

        <span v-if="$scopedSlots.metadata" class="rmr-site-picker__metadata">
          <slot name="metadata" :site="site" />
        </span>
      </button>
    </div>

    <div v-else class="rmr-site-picker__empty">
      {{ emptyText }}
    </div>

    <div v-if="!loading && pageCount > 1" class="rmr-site-picker__pagination">
      <span>
        Sites {{ pageStart }}–{{ pageEnd }} sur {{ filteredSites.length }}
      </span>
      <v-pagination
        v-model="page"
        :length="pageCount"
        :total-visible="5"
        :color="colors.indigo"
        circle
      />
    </div>
  </div>
</template>

<script>
import { mdiMagnify } from "@mdi/js";

import { RMR_INDIGO, RMR_INDIGO_BG, RMR_INDIGO_DARK } from "@/styles/tokens";

import RmrTextField from "./RmrTextField.vue";

const normalizeSearch = (value) =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default {
  name: "RmrSitePicker",
  components: { RmrTextField },
  props: {
    value: {
      type: [Number, String],
      default: null,
    },
    sites: {
      type: Array,
      default: () => [],
    },
    searchKeys: {
      type: Array,
      default: () => ["name", "domain"],
    },
    pageSize: {
      type: Number,
      default: 6,
      validator: (value) => value > 0,
    },
    searchPlaceholder: {
      type: String,
      default: "Rechercher un site ou un domaine…",
    },
    emptyText: {
      type: String,
      default: "Aucun site ne correspond à cette recherche.",
    },
    ariaLabel: {
      type: String,
      default: "Site",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      page: 1,
      icons: { mdiMagnify },
      colors: {
        indigo: RMR_INDIGO,
        indigoDark: RMR_INDIGO_DARK,
        indigoBackground: RMR_INDIGO_BG,
      },
    };
  },
  computed: {
    effectivePageSize() {
      if (this.$vuetify.breakpoint.xsOnly) return Math.min(this.pageSize, 3);
      if (this.$vuetify.breakpoint.smAndDown) return Math.min(this.pageSize, 4);
      return this.pageSize;
    },
    filteredSites() {
      const query = normalizeSearch(this.search);
      if (!query) return this.sites;
      return this.sites.filter((site) =>
        normalizeSearch(
          this.searchKeys.map((key) => site[key]).join(" ")
        ).includes(query)
      );
    },
    pageCount() {
      return Math.max(
        1,
        Math.ceil(this.filteredSites.length / this.effectivePageSize)
      );
    },
    paginatedSites() {
      const start = (this.page - 1) * this.effectivePageSize;
      return this.filteredSites.slice(start, start + this.effectivePageSize);
    },
    pageStart() {
      return this.filteredSites.length
        ? (this.page - 1) * this.effectivePageSize + 1
        : 0;
    },
    pageEnd() {
      return Math.min(
        this.page * this.effectivePageSize,
        this.filteredSites.length
      );
    },
  },
  watch: {
    search(value) {
      this.page = 1;
      if (!normalizeSearch(value)) this.showSelectedSite();
    },
    pageCount(count) {
      this.page = Math.min(this.page, count);
    },
    value: {
      immediate: true,
      handler() {
        this.showSelectedSite();
      },
    },
    sites() {
      this.showSelectedSite();
    },
    effectivePageSize() {
      this.showSelectedSite();
    },
  },
  methods: {
    initials(name) {
      return (name || "")
        .split(/\s+/)
        .map((word) => word.charAt(0))
        .join("")
        .slice(0, 3)
        .toUpperCase();
    },
    selectSite(siteId) {
      if (siteId === this.value) return;
      this.$emit("input", siteId);
      this.$emit("change", siteId);
    },
    showSelectedSite() {
      this.$nextTick(() => {
        const index = this.filteredSites.findIndex(
          (site) => site.id === this.value
        );
        if (index >= 0) {
          this.page = Math.floor(index / this.effectivePageSize) + 1;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.rmr-site-picker {
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-2xl;
  background: $rmr-surface-0;
}

.rmr-site-picker__toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px 0;
}

.rmr-site-picker__search {
  max-width: 480px;
  flex: 1 1 360px;
}

.rmr-site-picker__count,
.rmr-site-picker__pagination > span {
  flex: 0 0 auto;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
  font-weight: 750;
}

.rmr-site-picker__grid,
.rmr-site-picker__loading {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  padding: 18px 24px 24px;
}

.rmr-site-picker__card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 11px;
  min-width: 0;
  padding: 14px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
}

.rmr-site-picker__card:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: $rmr-indigo-border;
  box-shadow: $rmr-shadow-card;
}

.rmr-site-picker__card:focus-visible {
  outline: 3px solid rgba($rmr-indigo, 0.22);
  outline-offset: 2px;
}

.rmr-site-picker__card:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.rmr-site-picker__card--selected {
  border-color: $rmr-indigo;
  background: linear-gradient(180deg, $rmr-surface-0 0%, $rmr-indigo-bg 100%);
  box-shadow: 0 9px 26px rgba($rmr-indigo, 0.14);
}

.rmr-site-picker__avatar {
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: $rmr-radius-lg;
  font-size: $rmr-fs-md;
  font-weight: 900;
}

.rmr-site-picker__content {
  min-width: 0;
  padding-top: 2px;
}

.rmr-site-picker__content strong,
.rmr-site-picker__content small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rmr-site-picker__content strong {
  color: $rmr-text-primary;
  font-size: $rmr-fs-title;
}

.rmr-site-picker__content small {
  margin-top: 3px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

.rmr-site-picker__check {
  display: flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border: 1px solid $rmr-text-subtle;
  border-radius: 50%;
  color: transparent;
  font-size: $rmr-fs-md;
  font-weight: 900;
}

.rmr-site-picker__check--active {
  border-color: $rmr-indigo;
  background: $rmr-indigo;
  color: $rmr-surface-0;
}

.rmr-site-picker__metadata {
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  gap: 7px;
  margin-top: 2px;
  padding-top: 11px;
  border-top: 1px solid $rmr-border;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
  font-weight: 700;
}

.rmr-site-picker__empty {
  margin: 18px 24px 24px;
  padding: 20px;
  border: 1px dashed $rmr-text-subtle;
  border-radius: $rmr-radius-xl;
  color: $rmr-text-muted;
  font-size: $rmr-fs-md;
  text-align: center;
}

.rmr-site-picker__pagination {
  display: flex;
  min-height: 62px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 24px;
  border-top: 1px solid $rmr-border;
  background: $rmr-surface-1;
}

.rmr-site-picker__pagination ::v-deep .v-pagination {
  justify-content: flex-end;
}

.rmr-site-picker__pagination ::v-deep .v-pagination__item,
.rmr-site-picker__pagination ::v-deep .v-pagination__navigation {
  width: 32px;
  min-width: 32px;
  height: 32px;
  margin: 0 3px;
  box-shadow: none;
}

@media (max-width: 1000px) {
  .rmr-site-picker__grid,
  .rmr-site-picker__loading {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .rmr-site-picker__grid,
  .rmr-site-picker__loading {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 620px) {
  .rmr-site-picker__toolbar {
    align-items: stretch;
    flex-direction: column;
    padding: 18px 18px 0;
  }

  .rmr-site-picker__search {
    width: 100%;
    max-width: none;
    flex-basis: auto;
  }

  .rmr-site-picker__grid,
  .rmr-site-picker__loading {
    padding: 14px 18px 18px;
  }

  .rmr-site-picker__empty {
    margin: 14px 18px 18px;
  }

  .rmr-site-picker__pagination {
    align-items: flex-start;
    flex-direction: column;
    padding: 14px 18px;
  }
}
</style>
